import PropTypes from "prop-types"
import { Button, Flex, Form } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { CustomInput } from "ui/Input"
import { spacings } from "themes/variables"
import { normalizePageSet } from "helpers/powertranz"
import { MAX_CREDENTIAL_LENGTH, PAGE_SET_FORMAT } from "constants/powertranz"

// PowerTranz processing credentials + hosted page. Values live only in this antd form instance:
// never prefilled from the API (except the non-secret page names), never put in Redux or browser
// storage. The parent clears them after every submission, and leaving the tab or page unmounts the
// form (the tab pane is destroyed when hidden), which discards anything typed.
const CredentialsForm = ({ form, isReconnect, isSubmitting, isDisabled, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const requiredRule = { required: true, whitespace: true, message: t("powertranz.settings.fields.required") }
  // Same limits as the API's connect validation; the API stays authoritative.
  const maxLengthRule = { max: MAX_CREDENTIAL_LENGTH, message: t("powertranz.settings.fields.too_long", { max: MAX_CREDENTIAL_LENGTH }) }
  const pageSetRule = {
    validator: (_, value) =>
      !value?.trim() || PAGE_SET_FORMAT.test(normalizePageSet(value))
        ? Promise.resolve()
        : Promise.reject(new Error(t("powertranz.settings.fields.page_set_format")))
  }

  return (
    <CustomForm form={form} layout="vertical" onFinish={onSubmit} disabled={isDisabled} autoComplete="off">
      <Form.Item
        label={t("powertranz.settings.fields.powertranz_id")}
        name="powertranz_id"
        extra={t("powertranz.settings.fields.powertranz_id_hint")}
        rules={[requiredRule, maxLengthRule]}
      >
        <CustomInput autoComplete="off" spellCheck={false} />
      </Form.Item>
      <Form.Item
        label={t("powertranz.settings.fields.powertranz_password")}
        name="powertranz_password"
        extra={t("powertranz.settings.fields.powertranz_password_hint")}
        // Passwords can legitimately contain spaces, so only require a value.
        rules={[{ required: true, message: t("powertranz.settings.fields.required") }, maxLengthRule]}
      >
        <CustomInput.Password autoComplete="new-password" spellCheck={false} />
      </Form.Item>
      <Form.Item
        label={t("powertranz.settings.fields.page_set")}
        name="page_set"
        extra={t("powertranz.settings.fields.page_set_hint")}
        rules={[requiredRule, pageSetRule]}
      >
        <CustomInput autoComplete="off" spellCheck={false} />
      </Form.Item>
      <Form.Item
        label={t("powertranz.settings.fields.page_name")}
        name="page_name"
        extra={t("powertranz.settings.fields.page_name_hint")}
        rules={[requiredRule]}
      >
        <CustomInput autoComplete="off" spellCheck={false} />
      </Form.Item>
      <Flex wrap gap={spacings.S}>
        <Button size="large" type="primary" htmlType="submit" loading={isSubmitting} disabled={isDisabled}>
          {isReconnect ? t("powertranz.settings.save_credentials") : t("powertranz.settings.connect")}
        </Button>
        {onCancel && (
          <Button className="pr-l pl-l" color="primary" variant="outlined" size="large" onClick={onCancel} disabled={isSubmitting}>
            {t("global.cancel")}
          </Button>
        )}
      </Flex>
    </CustomForm>
  )
}

CredentialsForm.propTypes = {
  form: PropTypes.object.isRequired,
  isReconnect: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  // Shown only when the guide is changing credentials on an existing account.
  onCancel: PropTypes.func
}

export default CredentialsForm
